import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  hydrateAccountPending,
  installAccountPendingAutoSync,
} from '../services/accountPendingSync';
import { hydrateMonthlyChecks } from '../services/monthlyChecksSync';
import {
  hydrateUserSettings,
  installUserSettingsAutoSync,
} from '../services/userSettingsSync';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCachedUser = () => {
  try {
    const s = localStorage.getItem('auth_user');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [token] = useState(localStorage.getItem('token'));
  const cachedUser = getCachedUser();
  const [user, setUser] = useState(cachedUser);
  // Only show loading spinner if no cached user (first-time or logged out)
  const [loading, setLoading] = useState(!cachedUser && !!token);

  const axiosInstance = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const checkAuth = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('auth_user', JSON.stringify(response.data));
      // Pull authoritative state from the backend for the localStorage-
      // backed modules in the migration pipeline. Failure is non-fatal —
      // the user just sees stale local data until the next mount.
      hydrateAccountPending();
      hydrateMonthlyChecks();
      hydrateUserSettings();
    } catch (error) {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('auth_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Auto-sync listeners. Both are installed once at AuthProvider mount;
    // they are idempotent so re-mounts during HMR are safe.
    //  • account-pending: subscribes to `budget-pending-updated` events
    //  • user-settings: wraps localStorage.setItem to mirror SYNC_KEYS
    //                   writes into PUT /user-settings/{key}
    installAccountPendingAutoSync();
    installUserSettingsAutoSync();
  }, [checkAuth]);

  // ── localStorage clearing — STRICT WHITELIST ────────────────────────────────
  //
  // Historically clearUserData() removed every `patrimony_*` key, which also
  // wiped primary user data (budget allocations, monthly checks, family
  // goals, …) that lives in localStorage pending backend migration. That
  // caused real data loss reported by beta testers after logout/login.
  //
  // New rule: only API caches and one-shot UX flags are clearable. Anything
  // else stays — even at logout — so a user who logs back in on the same
  // device finds their state intact. Cross-user leak on shared devices is
  // accepted as a transitional risk: the in-flight migration to the backend
  // (PR2) eliminates it for good.
  //
  // To clear a key, add it to SAFE_TO_CLEAR (exact match) or SAFE_PREFIXES.
  // NEVER add a key here without confirming it is recomputable or trivial.
  const SAFE_TO_CLEAR = [
    'patrimony_dividend_yields_v2',                 // 24h cache (Yahoo)
    'patrimony_crypto_market_v1',                   // 5min cache (CoinGecko)
    'patrimony_screener_v3',                        // short-lived cache
    'patrimony_stocks_wishlist',                    // legacy, already migrated
    'patrimony_stocks_wishlist_migrated_v1',        // one-shot migration flag
    'patrimony_personal_periods_migrated_v1',       // one-shot migration flag
    'patrimony_budget_ideal_personal_migrated_v1',  // one-shot migration flag
    'patrimony_menage_sync_v1',                     // sync state (item_id only)
  ];
  const SAFE_PREFIXES = [
    'upgrade_session_dismissed_',                   // per-trigger UX dismissal
  ];

  const clearUserData = (reason = 'unspecified') => {
    const cleared = [];
    const skipped = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const matchesExact = SAFE_TO_CLEAR.includes(key);
      const matchesPrefix = SAFE_PREFIXES.some(p => key.startsWith(p));
      if (matchesExact || matchesPrefix) {
        cleared.push(key);
      } else if (key.startsWith('patrimony_')) {
        // Primary user data — explicitly preserved
        skipped.push(key);
      }
    }
    cleared.forEach(key => localStorage.removeItem(key));
    // eslint-disable-next-line no-console
    console.info(
      `[clearUserData] reason=${reason} cleared=${cleared.length} preserved=${skipped.length}`,
      { cleared, preserved: skipped }
    );
    window.dispatchEvent(new CustomEvent('user-session-cleared'));
  };

  // /auth/login, /auth/verify-email and /auth/session return a minimal
  // UserResponse that omits trial fields (plan / effective_plan / in_trial /
  // trial_end_date / trial_days_remaining). Without those, freshly-signed-up
  // users appear as Gratuit post-trial in the UI until a hard refresh —
  // which is the bug that the 14-day trial wasn't kicking in. Re-fetch
  // /auth/me with the new access token to hydrate the full user object.
  const fetchFullUser = async (accessToken, fallback) => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        withCredentials: true,
      });
      return r.data;
    } catch {
      return fallback;
    }
  };

  const login = async (email, password) => {
    // No clearUserData() at login — primary user data must survive the auth
    // round-trip. /auth/me below re-hydrates the user state with fresh data
    // from the backend.
    const response = await axiosInstance.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    const fullUser = await fetchFullUser(access_token, userData);
    localStorage.setItem('auth_user', JSON.stringify(fullUser));
    setUser(fullUser);
    // Pull localStorage-mirrored state from backend (account-pending,
    // monthly-checks) so cross-device sessions land on fresh data.
    hydrateAccountPending();
    hydrateMonthlyChecks();
    hydrateUserSettings();
    return fullUser;
  };

  const register = async (email, password, name, first_name, last_name) => {
    // Envoie le code de vérification, ne connecte pas encore l'utilisateur
    const response = await axiosInstance.post('/auth/register', { email, password, name, first_name, last_name });
    return response.data; // { message, email }
  };

  const verifyAndLogin = async (email, code) => {
    const response = await axiosInstance.post('/auth/verify-email', { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    const fullUser = await fetchFullUser(access_token, userData);
    localStorage.setItem('auth_user', JSON.stringify(fullUser));
    setUser(fullUser);
    hydrateAccountPending();
    hydrateMonthlyChecks();
    hydrateUserSettings();
    return fullUser;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axiosInstance.post('/auth/session', { session_id: sessionId });
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem('token', access_token);
    }
    const fullUser = access_token ? await fetchFullUser(access_token, userData) : userData;
    localStorage.setItem('auth_user', JSON.stringify(fullUser));
    setUser(fullUser);
    if (access_token) {
      hydrateAccountPending();
      hydrateMonthlyChecks();
      hydrateUserSettings();
    }
    return fullUser;
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (e) {
      // Ignore errors
    }
    clearUserData('logout');
    localStorage.removeItem('token');
    localStorage.removeItem('auth_user');
    setUser(null);
  };

  const forgotPassword = async (email) => {
    await axiosInstance.post('/auth/forgot-password', { email });
  };

  const changePassword = async (current_password, new_password) => {
    await axiosInstance.post('/auth/change-password', { current_password, new_password });
  };

  const submitOnboarding = async (interests) => {
    // axiosInstance captures the token at AuthProvider mount, so brand-new
    // signups (token absent at mount, set later by verifyAndLogin) hit a
    // request without an Authorization header — the POST silently 401s and
    // `onboarding_completed` stays `false` in MongoDB. The user then sees
    // the questionnaire reappear at every login.
    // Read the freshest token from localStorage and call axios directly to
    // avoid that closure trap. Mirrors the fetchFullUser pattern.
    const token = localStorage.getItem('token');
    await axios.post(`${API_URL}/api/auth/onboarding`, { interests }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    const updated = { ...user, onboarding_completed: true, interests };
    setUser(updated);
    localStorage.setItem('auth_user', JSON.stringify(updated));
  };

  const updatePreferences = async (prefs) => {
    // Same auth-header concern as submitOnboarding — use the freshest token.
    const token = localStorage.getItem('token');
    const r = await axios.put(`${API_URL}/api/users/me/preferences`, prefs, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    const merged = { ...user, ...prefs };
    setUser(merged);
    localStorage.setItem('auth_user', JSON.stringify(merged));
    return r.data;
  };

  const refreshUser = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('auth_user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      verifyAndLogin,
      loginWithGoogle,
      processGoogleSession,
      logout,
      refreshUser,
      submitOnboarding,
      updatePreferences,
      forgotPassword,
      changePassword,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
